import { useMutation, useQuery } from "@apollo/client";
import { Fragment, useEffect, useState } from "react";
import { Plus as PlusIcon, Search as SearchIcon } from "@tamagui/lucide-icons";
import { InsuranceStatus, type Payer } from "@medbillai/generated-graphql-types";
import { Avatar, Body3, Divider, Input, ListItem, Spinner, YStack } from "@medbillai/ui";
import { Flag } from "@medbillai/utils/flag";
import { type OnboardingNavProps, OnboardingScreen } from "../../components/OnboardingScreen";
import { captureUnreportedError } from "../../lib/apollo/error";
import { useBoolVariation } from "../../lib/flag";
import { type OnDone, tagged } from "../../lib/flow-engine";
import { useDebounced } from "../../lib/useDebounced";
import type { OnboardingIdentity } from "./types";
import { popularPayersQuery, recordInsuranceStatusMutation, searchPayersQuery } from "./utils/queries";
import { useRecordCarrier } from "./utils/useRecordCarrier";
export type InsuranceOutcomes = {
  selected: {
    carrier: string;
    payerId: string | null;
    /** The carrier is not recorded yet: the member ID step records both. */
    memberIdAsked: boolean;
  };
  uninsured: {};
  skipped: {};
};
const Outcome = tagged<InsuranceOutcomes>();
type PayerOption = Pick<Payer, "id" | "primaryPayerId" | "displayName" | "logoUrl" | "memberIdRequired">;

// No virtualization in the onboarding scroll region, so cap results.
const MAX_SEARCH_RESULTS = 30;

// Fade rows out at the bottom edge of the list region.
const LIST_EDGE_FADE_MASK = "linear-gradient(to bottom, black calc(100% - 20px), transparent 100%)";
const SEARCH_DEBOUNCE_MS = 250;
// Rounded down from the active payer catalog.
const SEARCHABLE_PAYERS = "3,500+";
const V3_NAV: OnboardingNavProps = {
  showLogo: false
};
export function Insurance({
  identity,
  nav = V3_NAV,
  subtitle,
  onDone
}: {
  /**
   * Absent when the run never passed through Profile — the carrier is then
   * recorded without a Member row.
   */
  identity?: OnboardingIdentity;
  nav?: OnboardingNavProps;
  subtitle?: string;
  onDone: OnDone<InsuranceOutcomes>;
}) {
  const [searchText, setSearchText] = useState("");
  // Blocks a second tap from landing a second record.
  const [isSaving, setIsSaving] = useState(false);
  const [namingCarrier, setNamingCarrier] = useState(false);
  const asksMemberId = useBoolVariation(Flag.INSURANCE_MEMBER_ID_ASK, false);
  const query = useDebounced(searchText.trim(), SEARCH_DEBOUNCE_MS);
  const popular = useQuery(popularPayersQuery);
  const search = useQuery(searchPayersQuery, {
    variables: {
      input: {
        query,
        limit: MAX_SEARCH_RESULTS
      }
    },
    skip: query === ""
  });
  const recordCarrier = useRecordCarrier(identity);
  const [recordInsuranceStatus] = useMutation(recordInsuranceStatusMutation);
  const isSearching = searchText.trim() !== "";
  const searchPending = query !== searchText.trim() || search.loading;
  // Keeps the last results on screen while the next query loads.
  const [settled, setSettled] = useState<readonly PayerOption[] | null>(null);
  useEffect(() => {
    if (!isSearching) {
      setSettled(null);
    } else if (!searchPending && search.data) {
      setSettled(search.data.searchPayers);
    }
  }, [isSearching, searchPending, search.data]);
  const results: readonly PayerOption[] = isSearching ? searchPending ? settled ?? [] : search.data?.searchPayers ?? [] : popular.data?.popularPayers ?? [];
  // Rows from the previous query stay visible but can't be picked.
  const stale = isSearching && searchPending;
  const loading = isSearching ? results.length === 0 && searchPending : popular.loading;
  const listError = isSearching ? search.error : popular.error;
  const noResults = isSearching && !loading && results.length === 0 && listError == null;
  const saveInBackground = (save: () => Promise<unknown>) => {
    setIsSaving(true);
    void save().catch(captureUnreportedError).finally(() => setIsSaving(false));
  };
  const pickCarrier = (carrier: string, payerId: string | null) => {
    if (isSaving) {
      return;
    }
    onDone(Outcome.selected({
      carrier,
      payerId,
      memberIdAsked: false
    }));
    saveInBackground(() => recordCarrier(carrier, payerId));
  };

  // Recorded so later surfaces stop asking for a card.
  const pickUninsured = () => {
    if (isSaving) {
      return;
    }
    onDone(Outcome.uninsured());
    saveInBackground(() => recordInsuranceStatus({
      variables: {
        input: {
          status: InsuranceStatus.UNINSURED
        }
      }
    }));
  };
  if (namingCarrier) {
    return <CarrierNameStep nav={nav} initialName={searchText.trim()} onSubmit={carrier => pickCarrier(carrier, null)} onBack={() => setNamingCarrier(false)} />;
  }
  return <OnboardingScreen contained data-sentry-element="OnboardingScreen" data-sentry-component="Insurance" data-sentry-source-file="Insurance.tsx">
      <OnboardingScreen.Nav {...nav} data-sentry-element="OnboardingScreen.Nav" data-sentry-source-file="Insurance.tsx" />
      <OnboardingScreen.Content data-sentry-element="OnboardingScreen.Content" data-sentry-source-file="Insurance.tsx">
        <OnboardingScreen.Header alignment="left" title="Select your insurance" subtitle={subtitle} data-sentry-element="OnboardingScreen.Header" data-sentry-source-file="Insurance.tsx" />
        <YStack gap="$sp2" flex={1} $platform-web={{
        minHeight: 0
      }} data-sentry-element="YStack" data-sentry-source-file="Insurance.tsx">
          <Input testID="select-insurance.search" label={`Search ${SEARCHABLE_PAYERS} insurers`} leftIcon={SearchIcon} value={searchText} onChangeText={setSearchText} autoCapitalize="none" autoComplete="off" autoCorrect={false} spellCheck={false} data-sentry-element="Input" data-sentry-source-file="Insurance.tsx" />
          {loading ? <YStack alignItems="center" paddingVertical="$sp4">
              <Spinner />
            </YStack> :
        // Web scrolls the list internally so the footer stays visible.
        <YStack $platform-web={{
          flexGrow: 1,
          flexShrink: 1,
          flexBasis: "auto",
          minHeight: 0,
          overflowY: "auto",
          maskImage: LIST_EDGE_FADE_MASK
        }}>
              {noResults ? <Body3 color="$textSubtle" paddingVertical="$sp2">
                  No results — try a different name.
                </Body3> : null}
              {results.map((payer, index) => <Fragment key={payer.id}>
                  {index > 0 ? <Divider thin /> : null}
                  <ListItem testID={`select-insurance.payer.${payer.primaryPayerId}`} leftElement={<Avatar size="small" shape="square" image={payer.logoUrl ?? undefined} initials={payer.displayName.charAt(0).toUpperCase()} />} title={payer.displayName} titleNumberOfLines={2} onPress={() => {
              if (stale) {
                return;
              }
              // The first check has to carry the ID, so the member
              // ID step records the carrier along with it. No check
              // runs without an identity, so there is no ID to ask for.
              if (asksMemberId && payer.memberIdRequired && identity !== undefined) {
                onDone(Outcome.selected({
                  carrier: payer.displayName,
                  payerId: payer.primaryPayerId,
                  memberIdAsked: true
                }));
              } else {
                pickCarrier(payer.displayName, payer.primaryPayerId);
              }
            }} />
                </Fragment>)}
              {isSearching ? <>
                  {results.length > 0 ? <Divider thin /> : null}
                  <ListItem testID="select-insurance.not-listed" leftElement={<Avatar size="small" shape="square" icon={PlusIcon} />} title="I don't see my insurance" onPress={() => setNamingCarrier(true)} />
                </> : null}
              {listError != null ? <Body3 color="$textNegative" role="alert" aria-live="polite">
                  Something went wrong, please try again.
                </Body3> : null}
            </YStack>}
        </YStack>
      </OnboardingScreen.Content>
      <OnboardingScreen.Footer data-sentry-element="OnboardingScreen.Footer" data-sentry-source-file="Insurance.tsx">
        <OnboardingScreen.SecondaryAction testID="select-insurance.uninsured" onPress={pickUninsured} data-sentry-element="OnboardingScreen.SecondaryAction" data-sentry-source-file="Insurance.tsx">
          I'm uninsured
        </OnboardingScreen.SecondaryAction>
        <OnboardingScreen.TertiaryAction testID="select-insurance.skip" onPress={() => onDone(Outcome.skipped())} data-sentry-element="OnboardingScreen.TertiaryAction" data-sentry-source-file="Insurance.tsx">
          Skip
        </OnboardingScreen.TertiaryAction>
      </OnboardingScreen.Footer>
    </OnboardingScreen>;
}
function CarrierNameStep({
  nav,
  initialName,
  onSubmit,
  onBack
}: {
  nav: OnboardingNavProps;
  initialName: string;
  onSubmit: (carrier: string) => void;
  onBack: () => void;
}) {
  const [name, setName] = useState(initialName);
  const carrier = name.trim();
  return <OnboardingScreen contained data-sentry-element="OnboardingScreen" data-sentry-component="CarrierNameStep" data-sentry-source-file="Insurance.tsx">
      <OnboardingScreen.Nav {...nav} data-sentry-element="OnboardingScreen.Nav" data-sentry-source-file="Insurance.tsx" />
      <OnboardingScreen.Content data-sentry-element="OnboardingScreen.Content" data-sentry-source-file="Insurance.tsx">
        <OnboardingScreen.Header alignment="left" title="What's your insurance called?" subtitle="Enter it as it appears on your insurance card." data-sentry-element="OnboardingScreen.Header" data-sentry-source-file="Insurance.tsx" />
        <Input testID="select-insurance.carrier-name" label="Insurance name" value={name} onChangeText={setName} autoFocus autoCorrect={false} data-sentry-element="Input" data-sentry-source-file="Insurance.tsx" />
      </OnboardingScreen.Content>
      <OnboardingScreen.Footer data-sentry-element="OnboardingScreen.Footer" data-sentry-source-file="Insurance.tsx">
        <OnboardingScreen.PrimaryAction testID="select-insurance.carrier-name.continue" variant="brand" disabled={carrier === ""} onPress={() => onSubmit(carrier)} data-sentry-element="OnboardingScreen.PrimaryAction" data-sentry-source-file="Insurance.tsx">
          Continue
        </OnboardingScreen.PrimaryAction>
        <OnboardingScreen.TertiaryAction testID="select-insurance.carrier-name.back" onPress={onBack} data-sentry-element="OnboardingScreen.TertiaryAction" data-sentry-source-file="Insurance.tsx">
          Back to search
        </OnboardingScreen.TertiaryAction>
      </OnboardingScreen.Footer>
    </OnboardingScreen>;
}